// HU-25 · «Registrar disposición» (`ui/MG-ILE-55.md`, Modal; reglas en `ui/MG-ILE-53.md`).
//
// Un solo modal para las dos entradas: la cabecera de «Disposiciones y seguimiento» (elige el
// caso) y la sección «Disposiciones de la autoridad» del Expediente (el caso viene fijado).
//
// LO QUE EL SERVIDOR DECIDE Y ESTA PANTALLA NO
// · La autoridad sale del caso y el «Registrada por …» de la sesión (HU-25-C1, C2; AT S3 D2):
//   no viajan en el cuerpo (con `extra="forbid"`, mandarlos es 422).
// · La canalización: registrar ES canalizar (RN-06); la pantalla no la calcula.
// · Ámbito (403, HU-25-C3), caso cerrado (409, C6) y no grave (409, C9): el texto del servidor
//   se pinta tal cual en el aviso de error de envío.
//
// LA AUTORIDAD QUE LA EMITIÓ (CC-02, B9; RN-78, CC2-25-C1; `ui/MG-ILE-1076.md`). Con un solo
// destino se completa sola y se ve de solo lectura, como siempre. Con varias cartas activas se
// elige entre ellas (las del trámite, `GET .../tramite`) y viaja como `autoridad_codigo`; sin
// elegirla no se envía. Si el trámite no se puede leer, el campo queda como hoy y el servidor
// dice «indica la autoridad» (409) en el aviso.
//
// EL OFICIO (HU-25-C7). Se sube en tres pasos —URL firmada, binario, asociar— contra la
// disposición recién registrada, con el `disposicion_id` que trae el 201 del registro
// (`DisposicionRegistradaSalida`). Ya no se busca la disposición en la lista de compromisos por
// descripción, responsable y plazo. Si el 201 no trae el id (un servidor anterior), no se adivina:
// se avisa que el oficio no se adjuntó.

import { useEffect, useState } from 'react';
import { FileText } from 'lucide-react';
import { ACEPTA, Alert, Button, Field, Input, Modal, Select, SubidaDeArchivo, Textarea } from './ui';
import type { OpcionDeSelect } from './ui';
import { asociarOficio, consultarBandejaRegulatoria, pedirUrlDelOficio, registrarDisposicion, verTramite } from '../api/regulatorio';
import { cartasActivas } from '../api/cartas';
import type { DisposicionNueva } from '../api/regulatorio';
import { subirBinario } from '../api/almacenamiento';
import { aFallo, DatosInvalidos } from '../api/cliente';
import type { Fallo } from '../api/cliente';
import { sha256DeArchivo } from '../lib/huella';

/** Textos de validación fijados por `ui/MG-ILE-55.md` (C4 de HU-25 los dos últimos). */
const MENSAJES = {
  caso: 'Indica el caso al que corresponde la disposición.',
  descripcion: 'Escribe qué ordenó la autoridad.',
  responsable: 'Indica el responsable: sin responsable no hay seguimiento',
  plazo: 'Indica el plazo: sin fecha no se puede seguir ni medir',
  // CC-02 (B9): el mismo texto del selector de autoridades de `ui/MG-ILE-1075.md`.
  autoridad_codigo: 'Elige la autoridad',
} as const;

/** CC-02 (B9): una autoridad destino del caso, para elegir quién emitió la disposición. */
type AutoridadDestino = { codigo: string; nombre: string };

/** Las autoridades de las cartas ACTIVAS del caso, por `orden`; vacía si no se pudo saber. */
async function destinosDelCaso(casoId: string): Promise<AutoridadDestino[]> {
  const tramite = await verTramite(casoId);
  return cartasActivas(tramite)
    .filter((c) => c.autoridad_codigo)
    .map((c) => ({ codigo: String(c.autoridad_codigo), nombre: c.autoridad_nombre ?? String(c.autoridad_codigo) }));
}

/** Tipo de la disposición (HU-25-C8, AF §5.2 definición 5). */
const TIPOS: OpcionDeSelect[] = [
  { value: 'disposicion', label: 'Disposición' },
  { value: 'solicitud_de_informacion', label: 'Solicitud de información' },
];

/** El caso al que va la disposición: id, número y autoridad (la del caso, de solo lectura). */
export type CasoDeLaDisposicion = { casoId: string; numero: string; autoridad: string | null };

/** El desenlace que se anuncia al cerrar el modal. */
export type DisposicionRegistrada = { casoId: string; aviso: string | null };

type Errores = Partial<Record<keyof typeof MENSAJES, string>>;

/** Los casos que se ofrecen: los de la bandeja regulatoria del ámbito (vacío anotado: no hay `GET /casos?disposicion_registrable`). */
async function casosDeLaBandeja(): Promise<CasoDeLaDisposicion[]> {
  const bandeja = await consultarBandejaRegulatoria();
  const vistos = new Map<string, CasoDeLaDisposicion>();
  for (const bloque of bandeja.bloques) {
    for (const f of bloque.filas) {
      if (!vistos.has(f.caso_id)) vistos.set(f.caso_id, { casoId: f.caso_id, numero: f.numero ?? f.caso_id, autoridad: f.autoridad });
    }
  }
  return [...vistos.values()];
}

/** HU-25-C7: sube el oficio y lo asocia a la disposición del 201. Devuelve el aviso si no se pudo. */
async function adjuntarOficio(casoId: string, disposicionId: string | null | undefined, archivo: File): Promise<string | null> {
  if (!disposicionId) {
    return 'La disposición quedó registrada, pero no se pudo identificar para adjuntarle el oficio. Adjúntalo de nuevo desde el expediente.';
  }
  const huella = await sha256DeArchivo(archivo);
  const permiso = await pedirUrlDelOficio(casoId, disposicionId, {
    nombre_archivo: archivo.name, huella_sha256: huella, tamano_bytes: archivo.size,
  });
  const rutaBlob = await subirBinario(archivo, permiso);
  await asociarOficio(casoId, disposicionId, {
    nombre_archivo: archivo.name, ruta_blob: rutaBlob, huella_sha256: huella,
    tipo_mime: archivo.type || 'application/octet-stream', tamano_bytes: archivo.size,
  });
  return null;
}

/** Los 422 de esquema traen el campo: van bajo él. El resto, al aviso. */
function erroresPorCampo(f: Fallo, error: unknown): Errores {
  if (!(error instanceof DatosInvalidos)) return {};
  const errores: Errores = {};
  for (const d of error.detalles as { campo: string; detalle: string }[]) {
    const campo = d.campo.split('.').pop() as keyof typeof MENSAJES;
    if (campo in MENSAJES) errores[campo] = d.detalle || f.message;
  }
  return errores;
}

export default function RegistrarDisposicion({ abierto, onCerrar, onRegistrada, casoFijo = null, testidDelError = 'seguimiento-error-envio' }: {
  abierto: boolean;
  onCerrar: () => void;
  onRegistrada: (r: DisposicionRegistrada) => void;
  /** Desde el Expediente: el caso viene dado y no se elige. */
  casoFijo?: CasoDeLaDisposicion | null;
  /** El testid del aviso de error de envío de la pantalla anfitriona (`seguimiento-` o `expediente-`). */
  testidDelError?: string;
}) {
  const [casos, setCasos] = useState<CasoDeLaDisposicion[] | null>(null);
  const [falloDeCasos, setFalloDeCasos] = useState<Fallo | null>(null);
  const [casoId, setCasoId] = useState('');
  const [tipo, setTipo] = useState<DisposicionNueva['tipo']>('disposicion');
  const [descripcion, setDescripcion] = useState('');
  const [responsable, setResponsable] = useState('');
  const [plazo, setPlazo] = useState('');
  const [fecha, setFecha] = useState('');
  const [archivo, setArchivo] = useState<File | null>(null);
  const [destinos, setDestinos] = useState<AutoridadDestino[]>([]);
  const [autoridad, setAutoridad] = useState('');
  const [errores, setErrores] = useState<Errores>({});
  const [fallo, setFallo] = useState<Fallo | null>(null);
  const [ocupado, setOcupado] = useState(false);

  // Al abrir: formulario limpio y, si el caso no viene dado, los casos del ámbito.
  useEffect(() => {
    if (!abierto) return undefined;
    setCasoId(casoFijo?.casoId ?? ''); setTipo('disposicion'); setDescripcion(''); setResponsable('');
    setPlazo(''); setFecha(''); setArchivo(null); setErrores({}); setFallo(null);
    if (casoFijo) return undefined;
    let vivo = true;
    setCasos(null); setFalloDeCasos(null);
    casosDeLaBandeja()
      .then((c) => { if (vivo) setCasos(c); })
      .catch((e) => { if (vivo) setFalloDeCasos(aFallo(e)); });
    return () => { vivo = false; };
  }, [abierto, casoFijo]);

  const elegido = casoFijo ?? casos?.find((c) => c.casoId === casoId) ?? null;
  const idDelElegido = elegido?.casoId ?? null;

  // CC-02 (B9): las autoridades a las que se notificó el caso elegido. Con más de una, se elige.
  useEffect(() => {
    setDestinos([]); setAutoridad('');
    if (!abierto || !idDelElegido) return undefined;
    let vivo = true;
    destinosDelCaso(idDelElegido)
      .then((d) => { if (vivo) setDestinos(d); })
      .catch(() => { if (vivo) setDestinos([]); });
    return () => { vivo = false; };
  }, [abierto, idDelElegido]);
  const variasAutoridades = destinos.length > 1;

  const sucio = Boolean(descripcion || responsable || plazo || fecha || archivo || autoridad);

  const validar = (): Errores => {
    const e: Errores = {};
    if (!elegido) e.caso = MENSAJES.caso;
    if (!descripcion.trim()) e.descripcion = MENSAJES.descripcion;
    if (!responsable.trim()) e.responsable = MENSAJES.responsable;
    if (!plazo) e.plazo = MENSAJES.plazo;
    if (variasAutoridades && !autoridad) e.autoridad_codigo = MENSAJES.autoridad_codigo;
    return e;
  };

  const guardar = async () => {
    const e = validar();
    setErrores(e);
    setFallo(null);
    if (Object.keys(e).length || !elegido) return;
    const datos: DisposicionNueva = {
      tipo, descripcion: descripcion.trim(), responsable: responsable.trim(), plazo, fecha: fecha || null,
      ...(variasAutoridades ? { autoridad_codigo: autoridad } : {}),
    };
    setOcupado(true);
    let disposicionId: string | null = null;
    try {
      disposicionId = (await registrarDisposicion(elegido.casoId, datos)).disposicion_id ?? null;
    } catch (error) {
      const f = aFallo(error);
      setErrores(erroresPorCampo(f, error));
      setFallo(f);
      setOcupado(false);
      return;
    }
    // La disposición ya existe: un oficio que falla no la deshace, se avisa.
    let aviso: string | null = null;
    if (archivo) {
      try {
        aviso = await adjuntarOficio(elegido.casoId, disposicionId, archivo);
      } catch (error) {
        aviso = `La disposición quedó registrada, pero el oficio no se adjuntó: ${aFallo(error).message}`;
      }
    }
    setOcupado(false);
    onRegistrada({ casoId: elegido.casoId, aviso });
  };

  const opcionesDeCaso: OpcionDeSelect[] = (casos ?? []).map((c) => ({ value: c.casoId, label: c.numero, hint: c.autoridad ?? undefined }));

  return (
    <Modal open={abierto} onClose={onCerrar} icon={FileText} sucio={sucio}
      eyebrow="Disposiciones y seguimiento" title="Registrar disposición" data-testid="disposicion-form"
      footer={<>
        <Button variant="secondary" onClick={onCerrar} data-testid="disposicion-form-cancelar">Cancelar</Button>
        <Button onClick={() => { guardar(); }} disabled={ocupado} data-testid="disposicion-form-guardar">
          {ocupado ? 'Registrando…' : 'Registrar disposición'}
        </Button>
      </>}>
      {fallo && !Object.keys(errores).length && (
        <div className="mb-3">
          <Alert tone={fallo.estado === 403 || fallo.estado === 409 ? 'warning' : 'danger'} title="No se registró la disposición"
            detalleTecnico={fallo.detalleTecnico} data-testid={testidDelError}>
            {fallo.message}
          </Alert>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
        <Field label="Caso" required error={errores.caso} className="sm:col-span-2">
          {casoFijo
            ? <Input value={casoFijo.numero} readOnly data-testid="disposicion-form-caso" />
            : (
              <Select buscable options={opcionesDeCaso} value={casoId} data-testid="disposicion-form-caso"
                placeholder={casos ? 'Elige el caso' : 'Cargando los casos…'} disabled={!casos}
                onChange={(e) => { setCasoId(String(e.target.value ?? '')); setErrores((x) => ({ ...x, caso: undefined })); }} />
            )}
        </Field>
        {falloDeCasos && (
          <div className="sm:col-span-2 mb-2">
            <Alert tone="warning" title="No se pudieron cargar los casos">{falloDeCasos.message}</Alert>
          </div>
        )}
        <Field label="Tipo" required>
          <Select sinVacio options={TIPOS} value={tipo} data-testid="disposicion-form-tipo"
            onChange={(e) => setTipo(e.target.value as DisposicionNueva['tipo'])} />
        </Field>
        {variasAutoridades
          ? (
            <Field label="Autoridad" required error={errores.autoridad_codigo}>
              <Select options={destinos.map((d) => ({ value: d.codigo, label: d.nombre }))} value={autoridad}
                data-testid="disposicion-form-autoridad"
                onChange={(e) => { setAutoridad(String(e.target.value ?? '')); setErrores((x) => ({ ...x, autoridad_codigo: undefined })); }} />
            </Field>
          )
          : (
            <Field label="Autoridad" hint="Sale del caso: no se edita.">
              <Input value={elegido?.autoridad ?? ''} readOnly data-testid="disposicion-form-autoridad" />
            </Field>
          )}
        <Field label="Descripción" required error={errores.descripcion} className="sm:col-span-2">
          <Textarea rows={3} maxLength={2000} value={descripcion} data-testid="disposicion-form-descripcion"
            onChange={(e) => { setDescripcion(e.target.value); setErrores((x) => ({ ...x, descripcion: undefined })); }} />
        </Field>
        <Field label="Responsable" required error={errores.responsable} className="sm:col-span-2">
          <Input maxLength={120} value={responsable} data-testid="disposicion-form-responsable"
            onChange={(e) => { setResponsable(e.target.value); setErrores((x) => ({ ...x, responsable: undefined })); }} />
        </Field>
        <Field label="Plazo" required error={errores.plazo}>
          <Input type="date" value={plazo} data-testid="disposicion-form-plazo"
            onChange={(e) => { setPlazo(e.target.value); setErrores((x) => ({ ...x, plazo: undefined })); }} />
        </Field>
        <Field label="Fecha de la disposición" hint="La del oficio de la autoridad.">
          <Input type="date" value={fecha} data-testid="disposicion-form-fecha" onChange={(e) => setFecha(e.target.value)} />
        </Field>
        <Field label="Documento de la autoridad" className="sm:col-span-2"
          hint="Queda en el expediente como «Comunicación de la autoridad».">
          <SubidaDeArchivo aria-label="Documento de la autoridad" archivo={archivo} onElegir={setArchivo}
            acepta={ACEPTA.salvoEjecutables} data-testid="disposicion-form-documento" />
        </Field>
      </div>
    </Modal>
  );
}
